import { useTranslation } from 'react-i18next';

import {
  type Board,
  boardColor,
  type Label,
  LabelChipPicker,
  VisibilityToggles,
} from '@/entities/board';
import { Button, FilterPopover } from '@/shared/ui';

import { CalendarSourcePicker } from './CalendarSourcePicker';
import styles from './CalendarFilters.module.css';
import { activeFilterCount, type CalendarState, visibilityOf } from './model';

interface FiltersProps {
  state: CalendarState;
  labels: readonly Label[];
  /** Workspace calendar: the boards and which of them are hidden. */
  boards?: readonly Board[];
  /** Global calendar: workspace names by id, so the board list is grouped by workspace. */
  workspaceNames?: ReadonlyMap<string, string>;
  /** The personal calendar lists active tasks only: there is no "completed" switch. */
  hideCompleted?: boolean;
  /** Archived tasks are shown (on by default); the choice is remembered per calendar. */
  showArchived?: boolean;
  /** Omitted: there is no such switch (the personal planner lists active tasks only). */
  onShowArchived?: (show: boolean) => void;
  onChange: (patch: Partial<CalendarState>) => void;
}

/**
 * The single "Filters" button of the Calendar. The popup holds the four primary switches (tasks,
 * events, completed, overdue) and the search / label / board filters. Filters only hide items in
 * this view; they are remembered per board / workspace and shown in the URL.
 */
export function CalendarFilters({
  state,
  labels,
  boards,
  workspaceNames,
  hideCompleted = false,
  showArchived = true,
  onShowArchived,
  onChange,
}: FiltersProps) {
  const { t } = useTranslation();
  return (
    <FilterPopover
      activeCount={activeFilterCount(state) + (showArchived || !onShowArchived ? 0 : 1)}
      onReset={() => {
        onShowArchived?.(true);
        onChange({
          showTasks: true,
          showEvents: true,
          includeCompleted: true,
          includeOverdue: true,
          search: '',
          labelIds: [],
          hiddenBoardIds: [],
        });
      }}
    >
      <VisibilityToggles
        hide={hideCompleted ? ['showCompleted'] : []}
        value={visibilityOf(state)}
        onChange={(patch) => {
          onChange({
            ...('showTasks' in patch && { showTasks: patch.showTasks }),
            ...('showEvents' in patch && { showEvents: patch.showEvents }),
            ...('showCompleted' in patch && { includeCompleted: patch.showCompleted }),
            ...('showOverdue' in patch && { includeOverdue: patch.showOverdue }),
          });
        }}
      />
      {onShowArchived && (
        <label className={styles.archivedSwitch}>
          <input
            type="checkbox"
            data-testid="show-archived"
            checked={showArchived}
            onChange={(event) => {
              onShowArchived(event.target.checked);
            }}
          />
          <span>{t('calendar.showArchived')}</span>
        </label>
      )}
      <input
        type="search"
        className={styles.search}
        aria-label={t('calendar.search')}
        placeholder={t('calendar.search')}
        value={state.search}
        maxLength={200}
        onChange={(event) => {
          onChange({ search: event.target.value });
        }}
      />
      {labels.length > 0 && (
        <div className={styles.group}>
          <span className={styles.legend}>{t('calendar.labels')}</span>
          <LabelChipPicker
            labels={labels}
            selectedIds={state.labelIds}
            label={t('calendar.labels')}
            emptyText=""
            onToggle={(label, selected) => {
              onChange({
                labelIds: selected
                  ? [...state.labelIds, label.id]
                  : state.labelIds.filter((id) => id !== label.id),
              });
            }}
          />
        </div>
      )}
      {boards &&
        boards.length > 1 &&
        (workspaceNames ? (
          <CalendarSourcePicker
            boards={boards}
            workspaceNames={workspaceNames}
            hiddenBoardIds={state.hiddenBoardIds}
            onChange={(hiddenBoardIds) => {
              onChange({ hiddenBoardIds });
            }}
          />
        ) : (
          <CalendarBoards
            boards={boards}
            hiddenBoardIds={state.hiddenBoardIds}
            onChange={(hiddenBoardIds) => {
              onChange({ hiddenBoardIds });
            }}
          />
        ))}
    </FilterPopover>
  );
}

interface BoardsProps {
  boards: readonly Board[];
  hiddenBoardIds: readonly string[];
  onChange: (hiddenBoardIds: string[]) => void;
}

/**
 * Workspace Calendar: which boards are shown (the global calendar uses {@link CalendarSourcePicker}). A hidden board stays in the list (so it can be
 * switched back on); this is calendar visibility only, unrelated to favorites.
 */
export function CalendarBoards({ boards, hiddenBoardIds, onChange }: BoardsProps) {
  const { t } = useTranslation();
  return (
    <section
      className={styles.boards}
      aria-label={t('calendar.boards')}
      data-testid="calendar-boards"
    >
      <div className={styles.boardsHead}>
        <h2 className={styles.boardsTitle}>{t('calendar.boards')}</h2>
        <Button
          size="sm"
          variant="ghost"
          disabled={hiddenBoardIds.length === 0}
          onClick={() => {
            onChange([]);
          }}
        >
          {t('calendar.showAll')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={hiddenBoardIds.length >= boards.length}
          onClick={() => {
            onChange(boards.map((board) => board.id));
          }}
        >
          {t('calendar.hideAll')}
        </Button>
      </div>
      <ul className={styles.boardList}>
        {boards.map((board) => {
          const shown = !hiddenBoardIds.includes(board.id);
          return (
            <li key={board.id}>
              <label className={`${styles.board} ${shown ? '' : styles.off}`}>
                <input
                  type="checkbox"
                  className={styles.boardCheck}
                  checked={shown}
                  onChange={() => {
                    onChange(
                      shown
                        ? [...hiddenBoardIds, board.id]
                        : hiddenBoardIds.filter((id) => id !== board.id),
                    );
                  }}
                />
                <span
                  className={styles.boardDot}
                  style={{
                    background: shown ? boardColor(board) : 'transparent',
                    borderColor: boardColor(board),
                  }}
                  aria-hidden
                />
                <span className={styles.boardName}>{board.name}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
